<script setup>
/* global axios */
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { messages } from './workflow-ui';
const props = defineProps({ conversationId: { type: Number, required: true } });
const route = useRoute();
const { t } = useI18n({ messages });
const owner = ref(null);
const error = ref('');
const busy = ref(false);
const agent = ref(null);
const team = ref(null);
let epoch = 0;
const button = 'rounded border border-n-weak px-2 py-1 text-xs disabled:opacity-40';
const field = 'w-full rounded border border-n-weak bg-n-background p-1 text-xs';
const agents = computed(() => {
  const members = owner.value?.teams.find(x => x.id === team.value)?.agent_ids;
  return (owner.value?.agents || []).filter(x => !team.value || members?.includes(x.id));
});
const name = computed(() => owner.value?.agents.find(x => x.id === owner.value.assignee_id)?.name);
async function request(action = '') {
  const version = ++epoch;
  busy.value = true;
  error.value = '';
  const base = `/api/v1/accounts/${route.params.accountId}/chat_flow_routing/conversations/${props.conversationId}`;
  try {
    const result = action ? await axios.post(`${base}/${action}`, {
      expected_assignee_id: owner.value.assignee_id, agent_id: agent.value, team_id: team.value,
    }) : await axios.get(base);
    if (epoch === version) owner.value = result.data;
  } catch (e) {
    if (epoch === version) {
      error.value = e.response?.data?.error || e.message;
      if (e.response?.data?.ownership) owner.value = e.response.data.ownership;
    }
  } finally {
    if (epoch === version) busy.value = false;
  }
}
async function resume() {
  if (window.confirm(t('WF.resumeConfirm'))) await request('resume');
}
watch(() => [route.params.accountId, props.conversationId], () => {
  owner.value = null;
  agent.value = null;
  team.value = null;
  request();
}, { immediate: true });
onBeforeUnmount(() => { epoch += 1; });
</script>

<template>
  <section class="space-y-2 border-b border-n-weak px-4 py-3">
    <div class="flex justify-between">
      <h3 class="text-sm font-semibold">{{ t('WF.teamOwnership') }}</h3>
      <button :class="button" :disabled="busy" @click="request()">{{ t('WF.refresh') }}</button>
    </div>
    <p v-if="error" role="alert" class="text-xs text-red-700">{{ error }}</p>
    <template v-if="owner">
      <p class="text-xs">{{ t('WF.currentOwner') }}: {{ name || (owner.assignee_id ? `#${owner.assignee_id}` : t('WF.unassigned')) }}</p>
      <button :class="button" :disabled="busy || Boolean(owner.assignee_id)" @click="request('claim')">{{ t('WF.claim') }}</button>
      <template v-if="owner.can_transfer">
        <select v-model="team" :class="field" @change="agent = null">
          <option :value="null">{{ t('WF.noTeam') }}</option>
          <option v-for="item in owner.teams" :key="item.id" :value="item.id">{{ item.name }}</option>
        </select>
        <select v-model="agent" :class="field">
          <option :value="null">{{ t('WF.selectAgent') }}</option>
          <option v-for="item in agents" :key="item.id" :value="item.id">{{ item.name }}</option>
        </select>
        <button :class="button" :disabled="busy || !agent" @click="request('transfer')">{{ t('WF.transfer') }}</button>
        <button v-if="owner.held" :class="button" :disabled="busy" @click="resume">{{ t('WF.resumeNext') }}</button>
      </template>
      <p class="text-xs text-n-slate-10">{{ t('WF.claimHelp') }}</p>
    </template>
  </section>
</template>
